import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildViewTicketInnerHtml } from '../lib/skyluxViewTicketHtml';
import { downloadTicketPdf, printTicketPdf, renderTicketPdfBlob } from '../lib/ticketPdf';
import { usePublicQuoteByTicket } from '../lib/usePublicQuoteByTicket';
import { sanitizeEticketNumber } from '../lib/skyluxReceiptIds';
import '../styles/view-ticket.css';

const DOWNLOAD_ICON =
  'https://www.skyluxtravel.com/mail-img?hash=ee52916d4ff118cf403c11b138f72ef3';
const PRINT_ICON =
  'https://www.skyluxtravel.com/mail-img?hash=0c75f427ccfe6d1cf191eb71669f3f8c';

const ViewTicketPage = () => {
  const { ticketNumber: rawTicketNumber } = useParams();
  const ticketNumber = sanitizeEticketNumber(rawTicketNumber) ?? rawTicketNumber?.trim() ?? null;
  const { quote, loading, error } = usePublicQuoteByTicket(ticketNumber);
  const contentRef = useRef<HTMLDivElement>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    document.title = quote
      ? `SkyLux Travel — E-Ticket ${sanitizeEticketNumber(quote.eTicketNumber) ?? ticketNumber ?? ''}`
      : 'SkyLux Travel — E-Ticket';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = 'SkyLux Travel';
      document.head.removeChild(meta);
    };
  }, [quote, ticketNumber]);

  const buildPdf = useCallback(async () => {
    const root = contentRef.current;
    if (!root) throw new Error('Ticket content is not ready.');
    return renderTicketPdfBlob(root);
  }, []);

  const onDownload = useCallback(async () => {
    if (!ticketNumber) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const blob = await buildPdf();
      downloadTicketPdf(blob, ticketNumber);
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : 'Could not generate PDF');
    } finally {
      setPdfBusy(false);
    }
  }, [buildPdf, ticketNumber]);

  const onPrint = useCallback(async () => {
    setPdfBusy(true);
    setPdfError(null);
    try {
      const blob = await buildPdf();
      printTicketPdf(blob);
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : 'Could not print ticket');
    } finally {
      setPdfBusy(false);
    }
  }, [buildPdf]);

  if (!ticketNumber || !sanitizeEticketNumber(ticketNumber)) {
    return (
      <div className="view-ticket-page">
        <main className="view-ticket-page__message">
          <p>Invalid ticket link. Open the View ticket button from your SkyLux e-ticket email.</p>
        </main>
      </div>
    );
  }

  if (loading && !quote) {
    return (
      <div className="view-ticket-page">
        <main className="view-ticket-page__message">
          <p>Loading your e-ticket…</p>
        </main>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="view-ticket-page">
        <main className="view-ticket-page__message">
          <p>This ticket could not be found.</p>
          <p className="view-ticket-page__muted">{error ?? 'Check the link from your email or contact your agent.'}</p>
        </main>
      </div>
    );
  }

  const innerHtml = buildViewTicketInnerHtml(quote, { mode: 'ticket' });

  return (
    <div className="view-ticket-page">
      <div
        ref={contentRef}
        id="view-ticket-document"
        className="view-ticket-page__document"
        dangerouslySetInnerHTML={{ __html: innerHtml }}
      />

      <footer className="view-ticket-footer" aria-label="Ticket actions">
        {pdfError ? <p className="view-ticket-footer__error">{pdfError}</p> : null}
        <div className="view-ticket-footer__bar">
          <button type="button" className="view-ticket-footer__btn view-ticket-footer__btn--download" disabled={pdfBusy} onClick={() => void onDownload()}>
            <img src={DOWNLOAD_ICON} alt="" width={18} height={18} />
            <span>{pdfBusy ? 'Preparing…' : 'Download'}</span>
          </button>
          <button type="button" className="view-ticket-footer__btn view-ticket-footer__btn--print" disabled={pdfBusy} onClick={() => void onPrint()}>
            <img src={PRINT_ICON} alt="" width={18} height={18} />
            <span>{pdfBusy ? 'Preparing…' : 'Print file'}</span>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ViewTicketPage;
